// src/config/plotConfigs/rrtsConfig.js

export const rrtsConfig = {
  sections: [
    {
      fields: [
        {
          id: "minReadLength", // This maps to "minread" in API
          label: "Min Read Length",
          type: "number",
          placeholder: "20",
          min: 1,
          max: 1000,
          tooltip:
            "The minimum read length (in nucleotides) used in the analysis.",
          width: 3,
        },
        {
          id: "maxReadLength", // This maps to "maxread" in API
          label: "Max Read Length",
          type: "number",
          placeholder: "150",
          min: 1,
          max: 1000,
          tooltip:
            "The maximum read length (in nucleotides) used in the analysis.",
          width: 3,
        },

        {
          id: "minCdsDensity",
          label: "Min CDS Density",
          type: "number",
          placeholder: "50",
          min: 0,
          tooltip:
            "CDS density is the count of the in-frame reads in the CDS region divided by the length of the CDS region. Transcripts with a CDS density below this value will be excluded.If comparison mode is used, both groups have to have an equal or higher cds density than this value to be included in the results.",
          width: 3,
        },
        {
          id: "minCdsCoverage",
          label: "Min CDS Coverage",
          type: "number",
          placeholder: "0.5",
          min: 0,
          max: 1,
          step: 0.1,
          tooltip:
            "CDS coverage is the percentage of positions in the CDS region that have at least one read (in-frame only). Transcripts with a CDS coverage value below this value will be excluded.If comparison mode is used, both groups have to have an equal or higher coverage than this value to be included in the results.",
          width: 3,
        },
        {
          id: "contextBefore",
          label: "Context Before",
          type: "text",
          tooltip:
            "Enter one or more nucleotides to filter transcripts where the nucleotides upstream of the stop codon match this value. Multiple values can be entered by seperating with a comma.",
          width: 3,
        },
        {
          id: "contextAfter",
          label: "Context After",
          type: "text",
          tooltip:
            "Enter one or more nucleotides to filter transcripts where the nucleotides downstream of the stop codon match this value.Multiple values can be entered by seperating with a comma.",
          width: 3,
        },
        {
          id: "minRtCount",
          label: "Min RT Count",
          type: "number",
          placeholder: "5",
          min: 0,
          tooltip:
            "Any transcript with a count in the readthrough region lower than this value will be excluded from the analysis. If comparison mode is used either group needs to pass this filter to be included in the results.",
          width: 3,
        },
        {
          id: "tranList",
          label: "Transcripts",
          type: "radio",
          options: [
            { value: "prin_trans", label: "Principal" }, // Updated to match API
            { value: "all_trans", label: "All" },
            { value: "custom_trans", label: "Custom" },
          ],
          tooltip:
            "Select either principal transcripts (there is only one transcript per gene), all transcripts or a custom list (enter a comma seperated list in the input box).",
          width: 3,
        },
        {
          id: "customTranList",
          label: "Custom Transcript List",
          type: "textarea",
          placeholder: "Enter transcript IDs separated by commas...",
          tooltip: "Enter custom transcript list when 'Custom' is selected",
          width: 12,
          rows: 3,
          showIf: (settings) => settings.tranList === "custom_trans",
        },
        {
          id: "uaaCheck",
          label: "UAA",
          type: "checkbox",
          tooltip:
            "If checked, transcripts with a UAA CDS stop codon will be included in the analysis.",
          width: 2,
        },
        {
          id: "uagCheck",
          label: "UAG",
          type: "checkbox",
          tooltip:
            "If checked, transcripts with a UAG CDS stop codon will be included in the analysis.",
          width: 2,
        },
        {
          id: "ugaCheck",
          label: "UGA",
          type: "checkbox",
          tooltip:
            "If checked, transcripts with a UGA CDS stop codon will be included in the analysis.",
          width: 2,
        },
        {
          id: "ambiguousReads",
          label: "Allow ambiguously mapped reads",
          type: "checkbox",
          tooltip:
            "If checked both unambiguous and ambiguously mapped reads will be used. If unchecked only unambiguously mapped reads will be used.",
          width: 6,
        },
        {
          id: "excludeRtStop",
          label: "Exclude RT stop",
          type: "checkbox",
          tooltip:
            "If checked the readthrough region stop codon will be excluded from calculations.",
          width: 6,
        },
        {
          id: "emailNotification",
          label: "Email notification",
          type: "checkbox",
          tooltip:
            "If checked an email notification will be sent to you once the job has completed. The email will contain a link to the results of this job.",
          width: 6,
        },
      ],
    },
  ],
};

export const rrtsDefaults = {
  ambiguousReads: false,
  tranList: "prin_trans", // Updated to match API
  customTranList: "",
  excludeRtStop: false,
  minRtCount: 30,
  contextBefore: "",
  uaaCheck: true,
  uagCheck: true,
  ugaCheck: true,
  contextAfter: "",
  minCdsDensity: 0.01,
  minCdsCoverage: 30,
  minReadLength: 20, // Maps to minread
  maxReadLength: 35, // Maps to maxread
  emailNotification: false,
};

export const rrtsValidation = {
  minReadLength: {
    required: true,
    min: 1,
    max: 1000,
    label: "Min read length",
  },
  maxReadLength: {
    required: true,
    min: 1,
    max: 1000,
    label: "Max read length",
  },
  minRtCount: { required: true, min: 0, label: "Min RT count" },
  // minCdsCoverage: { required: true, min: 0, max: 1, label: "Min CDS coverage" },
  custom: (value, allSettings) => {
    if (allSettings.minReadLength >= allSettings.maxReadLength) {
      return "Min read length must be less than max read length";
    }
    if (
      allSettings.tranList === "custom_trans" &&
      !allSettings.customTranList.trim()
    ) {
      return "Please provide custom transcript list";
    }
    if (
      !allSettings.uaaCheck &&
      !allSettings.uagCheck &&
      !allSettings.ugaCheck
    ) {
      return "Please select at least one stop codon";
    }
    return null;
  },
};
